import React, { useState, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import SignatureCanvas from "react-signature-canvas";
import Modal from "react-bootstrap/Modal";
import moment from "moment";
import FormSelect from "./FormSelect";
import FormInput from "./FormInput";
import { getFieldName, getFieldText } from "../../../scripts/helperScripts";
import { writeProcessLog } from "../../services/soar";

function Signature({
  signatures,
  clientData,
  collateralOptions,
  modalTitle,
  canvasWidth,
  canvasHeight,
  onSign,
  showModal,
  onHide,
  initialSignatureType,
  finalSignType,
  marginBottom = "300px",
  onlySignatures,
  header,
}) {
  const [sigCanvas, setSigCanvas] = useState({});
  const [signatureType, setSignatureType] = useState("");
  const [signatureRole, setSignatureRole] = useState("");
  const [clientOption, setClientOption] = useState();
  const [selectedContact, setSelectedContact] = useState("");
  const [providerSignDisabled, setProviderSignDisabled] = useState(false);
  const [errors, setErrors] = useState({});
  const [canvasSigned, setCanvasSigned] = useState(false);
  const [canvasSignDisabled, setCanvasSignDisabled] = useState(true);
  const [supervisorSignDisabled, setSupervisorSignDisabled] = useState(true);
  const location = useLocation();

  const handleClearSignature = (event) => {
    event.preventDefault();
    setCanvasSigned(false);
    sigCanvas.clear();
  };

  const handleSignatureRoleChange = (e) => {
    const signatureRole = e.currentTarget.value;
    setSignatureRole(signatureRole);
    const errs = { ...errors };
    if (signatureType === "supervisor" && !signatureRole) {
      setProviderSignDisabled(true);
    } else if (
      signatureType === "canvas" &&
      (!signatureRole || !selectedContact)
    ) {
      setCanvasSignDisabled(true);
      errs.signatureRole = "'Contact Role' is required.";
      setErrors(errs);
    } else {
      setProviderSignDisabled(false);
      setCanvasSignDisabled(false);
      delete errs.signatureRole;
      setErrors(errs);
    }
  };

  const handleSelect = (e) => {
    console.log("Selected Collateral", e.currentTarget);
    const selectedContact = e.currentTarget.value;
    if (clientOption?._id === selectedContact) {
      setSignatureRole(clientOption.contactRole);
    } else {
      setSignatureRole(
        collateralOptions.find((c) => c._id === selectedContact).contactRole
      );
    }
    setSelectedContact(selectedContact);
    const error = { ...errors };
    if (!selectedContact) {
      setCanvasSignDisabled(true);
      error.selectedContact = "'Client/Collateral' is required.";
      setErrors(error);
    } else {
      setCanvasSignDisabled(false);
      error.selectedContact = "";
      setErrors(error);
    }
  };

  const handleSign = (sigType, roleRequired) => {
    if (roleRequired && !signatureRole && sigType !== "canvas") {
      const errs = { ...errors };
      errs.signatureRole = "'Role' is required.";
      return setErrors(errs);
    }

    let sig = {};
    sig.datetime = new Date();
    setSignatureRole("");
    //    [RelatedId]
    //   ,[SubmissionId]
    //   ,[ObjectJSON]
    //   ,[ObjectType]
    //   ,[CreatedOn]
    //   ,[CreatedById]
    //   ,[CreatedByName]
    //   ,[ModifiedOn]
    //   ,[ModifiedById]
    //   ,[ModifiedByName]
    //   ,[Active]

    if (sigType === "canvas") {
      setCanvasSignDisabled(true);
      sig.canvas = sigCanvas.toDataURL("image/png");
      sig.signatureType = "Canvas";
      sig.signatureRole = signatureRole;
      sig.contactId = selectedContact;
      if (clientOption?._id === selectedContact) {
        sig.name = clientOption.contactName;
      } else {
        sig.name = collateralOptions.find(
          (c) => c._id === selectedContact
        ).contactName;
      }
      sigCanvas.clear();
    } else if (sigType === "provider") {
      sig.signatureType = "Provider";
      sig.signatureRole = signatureRole || "Provider";
      const user = JSON.parse(localStorage.getItem("safyInfo")).user;
      sig.userId = user.domainName;
      sig.name = user.displayName;
      if (user.safyInfo.SignatureCredentials)
        sig.name += `, ${user.safyInfo.SignatureCredentials}`;
    } else if (sigType === "supervisor") {
      sig.signatureType = "Supervisor";
      sig.signatureRole =
        signatureRole !== "" ? `Supervisor/${signatureRole}` : "Supervisor";
      const user = JSON.parse(localStorage.getItem("safyInfo")).user;
      sig.userId = user.domainName;
      sig.name = user.displayName;
      if (user.safyInfo.SignatureCredentials)
        sig.name += ` ${user.safyInfo.SignatureCredentials}`;
    }

    const formpath = location.pathname;
    const sigsubmissionid = formpath?.split("/")[4] || "pre-save";
    const sigLogData = { ...sig };
    delete sigLogData.canvas;

    const databaseOrg =
      process.env.REACT_APP_ENVIRONMENT === "Production"
        ? "ReactLive"
        : "ReactDev";

    const logData = {
      Type: "Signature",
      Location: databaseOrg,
      Description: formpath,
      Data: JSON.stringify(sigLogData),
      ObjectReferenceType: "Formsy Signature",
      ObjectReferenceId: sigsubmissionid,
    };

    writeProcessLog(logData);
    onSign(sig);
  };

  useEffect(() => {
    setSupervisorSignDisabled(
      signatures.filter(
        (s) =>
          s.signatureType === "Supervisor" &&
          s.userId ===
            JSON.parse(localStorage.getItem("safyInfo")).user.domainName
      ).length > 0
    );
  }, [signatures]);

  useEffect(() => {
    setProviderSignDisabled(
      signatures.filter(
        (s) =>
          s.signatureType === "Provider" &&
          s.userId ===
            JSON.parse(localStorage.getItem("safyInfo")).user.domainName
      ).length > 0
    );
  }, [signatures]);

  useEffect(() => {
    if (!clientData) {
      return;
    }

    const clientOption = {
      _id: clientData.id,
      name: clientData.fullName + " - Self",
      contactName: clientData.fullName,
      contactRole: "Self",
    };

    setClientOption(clientOption);
  }, [clientData]);

  const getFinalSignType = () => {
    const types = finalSignType
      .replace(/\s+/g, "")
      .replace("Canvas", "Client/Collateral")
      .split(",");

    if (types.length > 2) {
      return `${types.slice(0, types.length - 1).join(", ")}, or ${
        types[types.length - 1]
      }`;
    } else return types.join(" or ");
  };

  return (
    <>
      {showModal ? (
        <Modal show={showModal} animation={false}>
          <Modal.Header>
            <Modal.Title>{modalTitle}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>
              Type of Signature that locks down the form - {getFinalSignType()}
            </p>
            {initialSignatureType === "full" ? (
              <div className="container">
                <div className="h5">Sign As</div>
                <div className="row">
                  <div className="col">
                    <button
                      className={
                        signatureType === "supervisor"
                          ? "btn btn-secondary w-100"
                          : "btn btn-primary w-100"
                      }
                      onClick={(e) => {
                        e.preventDefault();
                        setSignatureType("supervisor");
                      }}
                    >
                      Provider/Supervisor
                    </button>
                  </div>
                  <div className="col">
                    <button
                      className={
                        signatureType === "canvas"
                          ? "btn btn-secondary w-100"
                          : "btn btn-primary w-100"
                      }
                      onClick={(e) => {
                        e.preventDefault();
                        setSignatureType("canvas");
                      }}
                    >
                      Client/Collateral
                    </button>
                  </div>
                </div>
              </div>
            ) : null}
            {initialSignatureType === "provider" ||
            signatureType === "provider" ? (
              <>
                <div className="float-start">
                  <button
                    className="btn btn-secondary m-2"
                    name="providerSignature"
                    disabled={providerSignDisabled}
                    onClick={(e) => {
                      e.preventDefault();
                      handleSign("provider");
                    }}
                  >
                    Sign as Provider
                  </button>
                </div>
              </>
            ) : null}
            {initialSignatureType === "supervisor" ||
            signatureType === "supervisor" ? (
              <>
                <FormInput
                  className="m-2"
                  name="signatureRole"
                  label={"Signature Role"}
                  onChange={handleSignatureRoleChange}
                  error={errors.signatureRole}
                  value={signatureRole}
                />
                <div className="clearfix">
                  <div className="float-start">
                    <button
                      className="btn btn-secondary m-2"
                      name="providerSignature"
                      disabled={providerSignDisabled}
                      onClick={(e) => {
                        e.preventDefault();
                        handleSign("provider", true);
                        setSignatureRole("");
                      }}
                    >
                      Sign as Provider
                    </button>
                  </div>
                  <div className="float-end">
                    <button
                      className="btn btn-secondary m-2"
                      name="supervisorSignature"
                      disabled={supervisorSignDisabled}
                      onClick={(e) => {
                        e.preventDefault();
                        handleSign("supervisor");
                        setSignatureRole("");
                      }}
                    >
                      Sign as Supervisor
                    </button>
                  </div>
                </div>
              </>
            ) : null}
            {signatureType === "canvas" ? (
              <>
                <div>
                  <FormSelect
                    className="m-2"
                    name="collateral"
                    label="Collateral"
                    options={
                      clientOption && collateralOptions?.length > 0
                        ? [clientOption, ...collateralOptions]
                        : clientOption
                        ? [clientOption]
                        : collateralOptions || []
                    }
                    onChange={handleSelect}
                    error={errors.selectedContact}
                  />
                  <FormInput
                    divClassName="m-2"
                    name="signatureRole"
                    label="Signature Role"
                    onChange={handleSignatureRoleChange}
                    error={errors.signatureRole}
                    value={signatureRole}
                  />
                </div>
                <div>
                  <div className="bg-white border border-dark d-inline-block m-2">
                    <SignatureCanvas
                      ref={(ref) => {
                        setSigCanvas(ref);
                      }}
                      canvasProps={{
                        width: canvasWidth,
                        height: canvasHeight,
                        className: "sigCanvas",
                      }}
                      onBegin={() => {
                        setCanvasSigned(true);
                      }}
                    ></SignatureCanvas>
                  </div>
                  <div>
                    <button
                      className="btn btn-primary m-2"
                      disabled={!canvasSigned || canvasSignDisabled}
                      onClick={(event) => {
                        event.preventDefault();
                        handleSign("canvas", true);
                      }}
                    >
                      Sign
                    </button>
                    <button
                      className="btn btn-secondary m-2"
                      onClick={handleClearSignature}
                    >
                      Clear Signature
                    </button>
                  </div>
                </div>
              </>
            ) : null}
          </Modal.Body>
          <Modal.Footer>
            <button className="btn btn-danger" onClick={onHide}>
              Close
            </button>
          </Modal.Footer>
        </Modal>
      ) : null}
      {onlySignatures ? (
        <>
          {signatures.map((s, index) => {
            return (
              <React.Fragment key={`signatures.${index}`}>
                <div>
                  {moment(s.datetime).format("M/D/YYYY H:mm a")}
                  {" - "}
                  {s.name}, {s.signatureRole ? s.signatureRole : ""}
                </div>
                {s.signatureType === "Canvas" && (
                  <div className="row" key={s.datetime + "Canvas"}>
                    <div className="col">
                      <div className="bg-white border border-dark d-inline-block m-2">
                        <img
                          style={{
                            height: canvasHeight / 2,
                            width: canvasWidth / 2,
                          }}
                          src={s.canvas}
                          alt={`${s.name} - ${s.signatureRole}`}
                        ></img>
                      </div>
                    </div>
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </>
      ) : (
        <div style={{ marginBottom: marginBottom }} className="mt-4">
          <div className="h4">{header || "Signatures"}</div>
          <table className="table table-sm">
            <thead>
              <tr>
                <th>Signature Date/Time</th>
                <th>Name</th>
                <th>Role</th>
              </tr>
            </thead>
            <tbody>
              {signatures.map((s, index) => {
                return (
                  <React.Fragment key={`signatures.${index}`}>
                    <tr>
                      <td>
                        {s.datetime.contains?.(" GMT-")
                          ? moment(
                              s.datetime.split(" (")[0],
                              "ddd MMM DD YYYY hh:mm:ss ZZ"
                            ).format("L")
                          : moment(s.datetime).format("L")}{" "}
                        {s.datetime.contains?.(" GMT-")
                          ? moment(
                              s.datetime.split(" (")[0],
                              "ddd MMM DD YYYY hh:mm:ss ZZ"
                            ).format("LT")
                          : moment(s.datetime).format("LT")}
                      </td>
                      <td>{s.name}</td>
                      <td>{s.signatureRole ? s.signatureRole : ""}</td>
                    </tr>
                    {s.signatureType === "Canvas" && (
                      <tr key={s.datetime + "Canvas"}>
                        <td colSpan="3">
                          <div className="bg-white border border-dark d-inline-block m-2">
                            <img
                              style={{
                                height: canvasHeight / 2,
                                width: canvasWidth / 2,
                              }}
                              src={s.canvas}
                              alt={`${s.name} - ${s.signatureRole}`}
                            ></img>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {/* <button
        className="btn btn-primary m-2"
        onClick={(e) => {
          e.preventDefault();
          showModal();
        }}
      >
        Sign
      </button> */}
    </>
  );
}

export default Signature;
